import React, { useState, useEffect } from "react";
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import AdminLayout from "../components/AdminLayout";
import { db } from "../firebase";
import { collection, getDocs } from 'firebase/firestore';
import {
  TrendingUp,
  Inventory as Package,
  People as Users,
  AttachMoney as DollarSign,
  BarChart as BarChart3,
  ShoppingCart,
  LocalOffer as Ticket,
  Warning as AlertCircle
} from '@mui/icons-material';

const DashboardContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: var(--space-xl);
`;

const DashboardHeader = styled('div')`
  margin-bottom: var(--space-lg);
`;

const WelcomeTitle = styled('h1')`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 var(--space-sm);
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const WelcomeSubtitle = styled('p')`
  font-size: 1.125rem;
  color: var(--text-secondary);
  margin: 0 0 var(--space-md);
  font-weight: 400;
`;

const LastUpdated = styled('p')`
  font-size: 0.875rem;
  color: var(--text-tertiary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: var(--space-sm);

  svg {
    width: 16px;
    height: 16px;
  }
`;

const StatsGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--space-lg);
  margin-bottom: var(--space-xl);
`;

const StatCard = styled('div')`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: var(--space-xl);
  position: relative;
  overflow: hidden;
  transition: all var(--transition-normal);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 35px var(--shadow);
    border-color: var(--accent-primary);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.color || 'var(--accent-gradient)'};
  }
`;

const StatHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--space-lg);
`;

const StatIcon = styled('div')`
  width: 48px;
  height: 48px;
  border-radius: var(--radius-md);
  background: ${props => props.bgColor || 'rgba(16, 185, 129, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.iconColor || 'var(--accent-primary)'};

  svg {
    width: 24px;
    height: 24px;
  }
`;

const StatChange = styled('div')`
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.neutral ? 'var(--text-tertiary)' : props.positive ? 'var(--success)' : 'var(--error)'};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const StatValue = styled('h3')`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 var(--space-xs);
`;

const StatLabel = styled('p')`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin: 0;
  font-weight: 500;
`;

const QuickActionsGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-lg);
  margin-bottom: var(--space-xl);
`;

const QuickActionCard = styled('button')`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: var(--space-lg);
  cursor: pointer;
  transition: all var(--transition-normal);
  text-align: left;
  font-family: inherit;
  color: var(--text-primary);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-md);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px var(--shadow);
    border-color: var(--accent-primary);
    background: var(--surface-hover);
  }

  &:active {
    transform: translateY(0);
  }
`;

const QuickActionIcon = styled('div')`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${props => props.bgColor || 'var(--accent-gradient)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  svg {
    width: 28px;
    height: 28px;
  }
`;

const QuickActionTitle = styled('h4')`
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  text-align: center;
`;

const QuickActionDesc = styled('p')`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0;
  text-align: center;
  line-height: 1.4;
`;

const RecentActivitySection = styled('section')`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: var(--space-xl);
`;

const SectionTitle = styled('h2')`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 var(--space-lg);
  display: flex;
  align-items: center;
  gap: var(--space-md);

  svg {
    width: 24px;
    height: 24px;
    color: var(--accent-primary);
  }
`;

const ActivityList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ActivityItem = styled('li')`
  display: flex;
  align-items: center;
  gap: var(--space-md);
  padding: var(--space-md) 0;
  border-bottom: 1px solid var(--border-subtle);

  &:last-child {
    border-bottom: none;
  }
`;

const ActivityIcon = styled('div')`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.bgColor || 'rgba(16, 185, 129, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.iconColor || 'var(--accent-primary)'};
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ActivityContent = styled('div')`
  flex: 1;
`;

const ActivityTitle = styled('p')`
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 var(--space-xs);
`;

const ActivityTime = styled('p')`
  font-size: 0.8rem;
  color: var(--text-tertiary);
  margin: 0;
`;

// Returns a date object from a Firestore Timestamp or plain date value
function toDate(raw) {
  if (!raw) return null;
  if (raw.toDate) return raw.toDate();
  const d = new Date(raw);
  return isNaN(d) ? null : d;
}

function timeAgo(date) {
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
}

function formatRM(amount) {
  return `RM ${amount.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function percentChange(current, previous) {
  if (previous === 0) return current > 0 ? { label: '+100%', positive: true } : { label: '--', neutral: true };
  const pct = ((current - previous) / previous) * 100;
  return {
    label: (pct >= 0 ? '+' : '') + pct.toFixed(1) + '%',
    positive: pct >= 0,
  };
}

const LOADING_STATS = [
  { label: "Total Revenue",  value: "Loading...", change: "--", neutral: true, positive: true, icon: <DollarSign />, color: "linear-gradient(135deg, #10b981 0%, #34d399 100%)", bgColor: "rgba(16, 185, 129, 0.1)", iconColor: "var(--accent-primary)" },
  { label: "Products",       value: "Loading...", change: "--", neutral: true, positive: true, icon: <Package />,    color: "linear-gradient(135deg, #06b6d4 0%, #38bdf8 100%)", bgColor: "rgba(6, 182, 212, 0.1)",  iconColor: "#06b6d4" },
  { label: "Active Users",   value: "Loading...", change: "--", neutral: true, positive: true, icon: <Users />,      color: "linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)", bgColor: "rgba(139, 92, 246, 0.1)", iconColor: "#8b5cf6" },
  { label: "Orders Today",   value: "Loading...", change: "--", neutral: true, positive: true, icon: <ShoppingCart />, color: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)", bgColor: "rgba(245, 158, 11, 0.1)", iconColor: "#f59e0b" },
];

function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(LOADING_STATS);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const now = new Date();
        const todayStart     = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const todayEnd       = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
        const yesterdayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);

        // Fetch all three collections in parallel
        const [purchasesSnap, productsSnap, usersSnap] = await Promise.all([
          getDocs(collection(db, 'purchases')),
          getDocs(collection(db, 'products')),
          getDocs(collection(db, 'users')),
        ]);

        // ── Purchases ──────────────────────────────────────────────────────
        const allPurchases = purchasesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        let totalRevenue = 0;
        let thisMonthRevenue = 0;
        let lastMonthRevenue = 0;
        let ordersToday = 0;
        let ordersYesterday = 0;

        allPurchases.forEach(p => {
          const amount = parseFloat(p.amount_paid ?? p.total_amount ?? 0) || 0;
          totalRevenue += amount;

          const date = toDate(p.purchase_date ?? p.timestamp ?? p.created_at);
          if (date) {
            if (date >= thisMonthStart)                          thisMonthRevenue += amount;
            if (date >= lastMonthStart && date < thisMonthStart) lastMonthRevenue += amount;
            if (date >= todayStart && date < todayEnd)           ordersToday++;
            if (date >= yesterdayStart && date < todayStart)     ordersYesterday++;
          }
        });

        // ── Products ───────────────────────────────────────────────────────
        const productsCount = productsSnap.size;

        // ── Users ──────────────────────────────────────────────────────────
        const allUsers = usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Count users not explicitly disabled (is_active may be missing on older records → treat as active)
        const activeUsers = allUsers.filter(u => u.is_active !== false).length;

        // ── Change metrics (real comparisons) ─────────────────────────────
        const revenueChg = percentChange(thisMonthRevenue, lastMonthRevenue);
        const ordersChg  = percentChange(ordersToday, ordersYesterday);

        setStats([
          {
            label: "Total Revenue",
            value: formatRM(totalRevenue),
            change: revenueChg.label,
            positive: revenueChg.positive,
            neutral: revenueChg.neutral,
            icon: <DollarSign />,
            color: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
            bgColor: "rgba(16, 185, 129, 0.1)",
            iconColor: "var(--accent-primary)"
          },
          {
            label: "Products",
            value: productsCount.toLocaleString(),
            change: "--",
            neutral: true,
            positive: true,
            icon: <Package />,
            color: "linear-gradient(135deg, #06b6d4 0%, #38bdf8 100%)",
            bgColor: "rgba(6, 182, 212, 0.1)",
            iconColor: "#06b6d4"
          },
          {
            label: "Active Users",
            value: activeUsers.toLocaleString(),
            change: "--",
            neutral: true,
            positive: true,
            icon: <Users />,
            color: "linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)",
            bgColor: "rgba(139, 92, 246, 0.1)",
            iconColor: "#8b5cf6"
          },
          {
            label: "Orders Today",
            value: ordersToday.toString(),
            change: ordersChg.label,
            positive: ordersChg.positive,
            neutral: ordersChg.neutral,
            icon: <ShoppingCart />,
            color: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
            bgColor: "rgba(245, 158, 11, 0.1)",
            iconColor: "#f59e0b"
          }
        ]);

        // ── Recent Activity ────────────────────────────────────────────────
        // Last 6 purchases sorted newest-first
        const recentPurchases = allPurchases
          .map(p => ({ ...p, _date: toDate(p.purchase_date ?? p.timestamp ?? p.created_at) }))
          .filter(p => p._date)
          .sort((a, b) => b._date - a._date)
          .slice(0, 6)
          .map(p => ({
            title: `Order completed · ${formatRM(parseFloat(p.amount_paid ?? p.total_amount ?? 0) || 0)}`,
            date: p._date,
            icon: <ShoppingCart />,
            bgColor: "rgba(6, 182, 212, 0.1)",
            iconColor: "#06b6d4"
          }));

        // Last 3 user registrations sorted newest-first
        const recentUsers = allUsers
          .map(u => ({ ...u, _date: toDate(u.joined_date ?? u.created_at) }))
          .filter(u => u._date)
          .sort((a, b) => b._date - a._date)
          .slice(0, 3)
          .map(u => ({
            title: `New user joined: ${u.email || u.name || 'Unknown'}`,
            date: u._date,
            icon: <Users />,
            bgColor: "rgba(139, 92, 246, 0.1)",
            iconColor: "#8b5cf6"
          }));

        const activity = [...recentPurchases, ...recentUsers]
          .sort((a, b) => b.date - a.date)
          .slice(0, 8)
          .map(item => ({ ...item, time: timeAgo(item.date) }));

        setRecentActivity(activity);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    fetchDashboardData();
  }, []);

  const quickActions = [
    {
      title: "Add Product",
      description: "Create a new product listing",
      icon: <Package />,
      bgColor: "var(--accent-gradient)",
      action: () => navigate('/products')
    },
    {
      title: "Manage Users",
      description: "View and manage user accounts",
      icon: <Users />,
      bgColor: "linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)",
      action: () => navigate('/users')
    },
    {
      title: "View Reports",
      description: "Check analytics and insights",
      icon: <BarChart3 />,
      bgColor: "linear-gradient(135deg, #06b6d4 0%, #38bdf8 100%)",
      action: () => navigate('/reports')
    },
    {
      title: "Create Voucher",
      description: "Set up discount campaigns",
      icon: <Ticket />,
      bgColor: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
      action: () => navigate('/vouchers')
    }
  ];

  return (
    <AdminLayout>
      <DashboardContainer>
        <DashboardHeader>
          <WelcomeTitle>Dashboard Overview</WelcomeTitle>
          <WelcomeSubtitle>
            Welcome back! Here's what's happening with your GROLEE store today.
          </WelcomeSubtitle>
          <LastUpdated>
            <AlertCircle />
            Last updated: {new Date().toLocaleString()}
          </LastUpdated>
        </DashboardHeader>

        <StatsGrid>
          {stats.map((stat, index) => (
            <StatCard key={index} color={stat.color}>
              <StatHeader>
                <StatIcon bgColor={stat.bgColor} iconColor={stat.iconColor}>
                  {stat.icon}
                </StatIcon>
                <StatChange positive={stat.positive} neutral={stat.neutral}>
                  <TrendingUp style={{
                    transform: (!stat.neutral && !stat.positive) ? 'rotate(180deg)' : 'none'
                  }} />
                  {stat.change}
                </StatChange>
              </StatHeader>
              <StatValue>{stat.value}</StatValue>
              <StatLabel>{stat.label}</StatLabel>
            </StatCard>
          ))}
        </StatsGrid>

        <SectionTitle>
          <ShoppingCart />
          Quick Actions
        </SectionTitle>

        <QuickActionsGrid>
          {quickActions.map((action, index) => (
            <QuickActionCard key={index} onClick={action.action}>
              <QuickActionIcon bgColor={action.bgColor}>
                {action.icon}
              </QuickActionIcon>
              <QuickActionTitle>{action.title}</QuickActionTitle>
              <QuickActionDesc>{action.description}</QuickActionDesc>
            </QuickActionCard>
          ))}
        </QuickActionsGrid>

        <RecentActivitySection>
          <SectionTitle>
            <BarChart3 />
            Recent Activity
          </SectionTitle>
          <ActivityList>
            {recentActivity.length === 0 ? (
              <ActivityItem>
                <ActivityContent>
                  <ActivityTitle style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>
                    No recent activity found.
                  </ActivityTitle>
                </ActivityContent>
              </ActivityItem>
            ) : (
              recentActivity.map((activity, index) => (
                <ActivityItem key={index}>
                  <ActivityIcon
                    bgColor={activity.bgColor}
                    iconColor={activity.iconColor}
                  >
                    {activity.icon}
                  </ActivityIcon>
                  <ActivityContent>
                    <ActivityTitle>{activity.title}</ActivityTitle>
                    <ActivityTime>{activity.time}</ActivityTime>
                  </ActivityContent>
                </ActivityItem>
              ))
            )}
          </ActivityList>
        </RecentActivitySection>
      </DashboardContainer>
    </AdminLayout>
  );
}

export default Dashboard;
